import React from "react";
import { Tooltip } from "antd";
import { Localize } from "components/service";
import { timeFormat } from "utils/helper-functions";
import { gqlBuilderWhere } from "utils";
import SettingsConst from "../settings-const";


const STATUS = SettingsConst.translationAiLanguageCacheStatus;
const languagesBySlug = SettingsConst.translationAiLanguages.reduce((result, language) => ({
    ...result,
    [ language.slug ]: language,
}), {});

const labelRequestVariables = {
    first: 500,
    page: 1,
    where: gqlBuilderWhere({
        promptType: {
            column: "PROMPT_TYPE",
            operator: "EQ",
            value: "label",
        }
    }),
    orderBy: [ { column: "CREATED_AT", order: "DESC" } ],
};

const statusMeta = {
    [ STATUS.static ]: {
        label: 'TABLES.Value_LanguageCacheStatic',
        tooltip: 'TABLES.Tooltip_LanguageCacheStatic',
    },
    [ STATUS.ready ]: {
        label: 'TABLES.Value_LanguageCacheReady',
        tooltip: 'TABLES.Tooltip_LanguageCacheReady',
    },
    [ STATUS.loaded ]: {
        label: 'TABLES.Value_LanguageCacheLoaded',
        tooltip: 'TABLES.Tooltip_LanguageCacheLoaded',
    },
    [ STATUS.notRequested ]: {
        label: 'TABLES.Value_LanguageCacheNotRequested',
        tooltip: 'TABLES.Tooltip_LanguageCacheNotRequested',
    },
};

const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
};

const getLatestLabelRequestByLanguage = (records = []) => (
    [ ...records ]
        .filter((record) => record?.prompt_type === 'label' && record?.lang_target)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .reduce((result, record) => {
            const slug = String(record.lang_target).toLowerCase();

            return result[ slug ] ? result : {
                ...result,
                [ slug ]: record,
            };
        }, {})
);

const getRuntimeStatus = (request) => {
    if (!request)
    {
        return STATUS.notRequested;
    }

    const itemsCount = toNumber(request.items_count),
          cacheHits = toNumber(request.cache_hit_items_count);

    return itemsCount > 0 && cacheHits >= itemsCount ? STATUS.ready : STATUS.loaded;
};

const buildStatuses = (records = []) => {
    const latestByLanguage = getLatestLabelRequestByLanguage(records);

    return SettingsConst.translationAiLanguages.map((language) => {
        const isStatic = SettingsConst.translationAiStaticLanguages.some(({ slug }) => slug === language.slug);
        const request = latestByLanguage[ language.slug ];
        const status = isStatic ? STATUS.static : getRuntimeStatus(request);

        return {
            ...language,
            request,
            status,
        };
    });
};

const toMap = (statuses = []) => (
    statuses.reduce((result, item) => ({
        ...result,
        [ item.slug ]: item,
    }), {})
);

const getLanguage = (value) => (
    value ? languagesBySlug[ String(value).toLowerCase() ] : null
);

const getLanguageLabel = (value) => (
    getLanguage(value)?.label || (value ? String(value).toUpperCase() : '')
);

const renderStatusBadge = (status) => {
    const meta = getStatusMeta(status);

    return (
        <Tooltip title={ <Localize>{ meta.tooltip }</Localize> }>
            <span className={ `translation-ai-language-cache-badge ${ status }` }>
                <Localize>{ meta.label }</Localize>
            </span>
        </Tooltip>
    );
};

const getStatusMeta = (status) => (
    statusMeta[ status ] || statusMeta[ STATUS.notRequested ]
);

const renderRequestDate = (request) => (
    request?.created_at ? timeFormat(request.created_at) : <strong>&mdash;</strong>
);

const TranslationAiLanguageCacheHelper = {
    STATUS,
    labelRequestVariables,
    buildStatuses,
    toMap,
    getLanguage,
    getLanguageLabel,
    getStatusMeta,
    renderStatusBadge,
    renderRequestDate,
};

export default TranslationAiLanguageCacheHelper;
